import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, RefreshCw, Trash2, MessageCircle, Phone, Mail, MapPin, Tag } from "lucide-react";

const STAGES = [
  { key: "new",          label: "New",          color: "from-[#FF0066]/20 to-[#FF0066]/5 text-[#FF0066] border-[#FF0066]/40" },
  { key: "contacted",    label: "Contacted",    color: "from-blue-500/20 to-blue-500/5 text-blue-300 border-blue-400/40" },
  { key: "demo_booked",  label: "Demo Booked",  color: "from-amber-500/20 to-amber-500/5 text-amber-300 border-amber-400/40" },
  { key: "enrolled",     label: "Enrolled",     color: "from-emerald-500/20 to-emerald-500/5 text-emerald-300 border-emerald-400/40" },
  { key: "lost",         label: "Lost",         color: "from-white/10 to-white/5 text-white/50 border-white/15" },
];

export default function LeadsAdminPanel() {
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (stageFilter) params.stage = stageFilter;
      if (courseFilter) params.course = courseFilter;
      if (q.trim()) params.q = q.trim();
      const { data: d } = await api.get("/leads", { params });
      setData(d);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stageFilter, courseFilter]);

  const updateStage = async (id, stage) => {
    try {
      await api.patch(`/leads/${id}`, { stage });
      toast.success("Stage updated");
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try { await api.delete(`/leads/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="leads-admin-panel">
      {/* Pipeline counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {STAGES.map((s) => (
          <button
            key={s.key}
            onClick={() => setStageFilter(stageFilter === s.key ? "" : s.key)}
            className={`relative rounded-xl p-4 text-left border bg-gradient-to-br ${s.color} ${stageFilter === s.key ? "ring-2 ring-[#FF0066]" : ""} hover:scale-[1.02] transition`}
            data-testid={`leads-stage-${s.key}`}
          >
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{s.label}</div>
            <div className="text-2xl font-bold mt-1">{data.counts[s.key] ?? 0}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Search name / mobile / email / city…"
            className="w-full bg-[#0a0a0a] border border-white/15 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]"
            data-testid="leads-search"
          />
        </div>
        <input
          value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}
          placeholder="Filter by course"
          className="bg-[#0a0a0a] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]"
          data-testid="leads-course-filter"
        />
        <button onClick={load} className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-sm text-white" data-testid="leads-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
        {(stageFilter || courseFilter || q) && (
          <button onClick={() => { setStageFilter(""); setCourseFilter(""); setQ(""); }} className="text-xs text-white/50 hover:text-white">Clear filters</button>
        )}
      </div>

      {/* Leads table */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-white/40">Loading leads…</div>
        ) : data.items.length === 0 ? (
          <div className="p-8 text-center text-white/50">
            <Tag className="w-8 h-8 mx-auto text-white/20 mb-2" />
            No leads found. New enquiries will appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-white/5 text-[10px] uppercase tracking-wider text-white/50">
                <tr>
                  <th className="text-left px-4 py-2.5">Lead</th>
                  <th className="text-left px-4 py-2.5">Course</th>
                  <th className="text-left px-4 py-2.5">Goal · Budget · City</th>
                  <th className="text-left px-4 py-2.5">Source</th>
                  <th className="text-left px-4 py-2.5">Stage</th>
                  <th className="text-left px-4 py-2.5">Created</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.items.map((l) => (
                  <tr key={l.id} className="hover:bg-white/[0.02]" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-white">{l.name || <span className="text-white/40">(partial)</span>}</div>
                      <div className="text-[11px] text-white/60 flex flex-col gap-0.5 mt-0.5">
                        {l.mobile && <span className="inline-flex items-center gap-1"><Phone className="w-2.5 h-2.5" /> {l.mobile}</span>}
                        {l.email && <span className="inline-flex items-center gap-1"><Mail className="w-2.5 h-2.5" /> {l.email}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-white/80 text-xs max-w-[180px]">{l.course_interest || "—"}</td>
                    <td className="px-4 py-3 align-top text-[11px] text-white/65">
                      <div className="flex flex-col gap-0.5">
                        {l.career_goal && <span>🎯 {l.career_goal}</span>}
                        {l.budget && <span>💰 {l.budget}</span>}
                        {l.city && <span className="inline-flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {l.city}</span>}
                        {l.qualification && <span className="text-white/50">{l.qualification}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className="text-[10px] uppercase tracking-wider text-white/50 bg-white/5 border border-white/10 rounded px-1.5 py-0.5">{l.source || "website"}</span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <select
                        value={l.stage || "new"}
                        onChange={(e) => updateStage(l.id, e.target.value)}
                        className="bg-[#050505] border border-white/15 rounded-md px-2 py-1 text-xs text-white focus:outline-none focus:border-[#FF0066]"
                        data-testid={`lead-stage-select-${l.id}`}
                      >
                        {STAGES.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
                      </select>
                    </td>
                    <td className="px-4 py-3 align-top text-[11px] text-white/50 whitespace-nowrap">
                      {l.created_at ? new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {l.mobile && (
                          <a href={`https://wa.me/91${l.mobile.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hi ${l.name || ""}, this is Kamlesh Sir's team about your ${l.course_interest || "course"} enquiry.`)}`}
                             target="_blank" rel="noreferrer" className="w-7 h-7 rounded-md bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/30 flex items-center justify-center" title="WhatsApp">
                            <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />
                          </a>
                        )}
                        {l.mobile && (
                          <a href={`tel:${l.mobile}`} className="w-7 h-7 rounded-md bg-blue-500/15 hover:bg-blue-500/25 border border-blue-400/30 flex items-center justify-center" title="Call">
                            <Phone className="w-3.5 h-3.5 text-blue-300" />
                          </a>
                        )}
                        <button onClick={() => removeLead(l.id)} className="w-7 h-7 rounded-md bg-red-500/10 hover:bg-red-500/25 border border-red-400/30 flex items-center justify-center" title="Delete" data-testid={`lead-delete-${l.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-300" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
